import React from "react";
import { FaCheckCircle, FaRegArrowAltCircleDown } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import Confetti from "react-confetti";
import { motion } from "framer-motion";
import queryString from "query-string";
import { Tooltip } from "react-tooltip";
import generateAndDownloadReceipt from "./pdfgen";

function PaymentSuccess() {
  // Countdown timer state
  const navigate = useNavigate();
  const location = useLocation();

  const onContinue = () => {
    navigate("/login");
  };

  // Extract query parameters
  const {
    amount,
    payment_id,
    currency,
    plan,
    email,
    phone,
    id,
    method,
    create_at,
  } = queryString.parse(location.search);

  return (
    <div className="min-h-screen min-w-screen flex gap-2 items-center justify-center bg-gradient-to-r from-slate-700 via-slate-300 to-slate-950 relative overflow-hidden py-5">
      {/* Confetti Animation - Only show when showConfetti is true */}
      <Confetti recycle={true} numberOfPieces={150} />

      <motion.div
        className="bg-white shadow-lg rounded-lg max-w-md h-full text-center relative z-10 p-4 flex flex-col gap-2 "
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}>
        {/* Success Icon */}
        <motion.div
          className="flex justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 150, damping: 20 }}>
          <FaCheckCircle className="h-16 w-16 text-lime-500" />
        </motion.div>
        <motion.div
          className="absolute right-5 cursor-pointer"
          id="tooltipdownload"
          onClick={() =>
            generateAndDownloadReceipt(
              amount,
              payment_id,
              currency,
              plan,
              email,
              phone,
              id,
              method,
              create_at
            )
          }
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 150, damping: 20 }}>
          <FaRegArrowAltCircleDown className="h-6 w-6 text-slate-950" />
        </motion.div>
        <Tooltip
          anchorSelect={`#tooltipdownload`}
          place="left-end"
          variant="dark"
          float={true}>
          <span>Download Receipt</span>
        </Tooltip>

        {/* Success Message */}
        <motion.h2
          className="text-3xl font-bold text-gray-800"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}>
          Payment Successful!
        </motion.h2>
        <motion.p
          className="text-gray-600 "
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}>
          Thank you for your payment. Your transaction has been processed
          successfully.
        </motion.p>

        {/* Payment Details */}
        <motion.div
          className="bg-gray-200 p-4 rounded-md shadow-inner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}>
          <h3 className="text-lg font-semibold text-gray-700">
            Payment Details
          </h3>
          <ul className="text-left text-gray-600">
            <li className="flex justify-between py-1">
              <span>Payment ID:</span>
              <span>{payment_id || "N/A"}</span>
            </li>
            <li className="flex justify-between py-1">
              <span>Amount:</span>
              <span>{amount ? `${currency} ${amount}` : "N/A"}</span>
            </li>
            <li className="flex justify-between py-1">
              <span>Status:</span>
              <span className="text-lime-600 font-semibold">Success</span>
            </li>
          </ul>
        </motion.div>

        {/* Call to Action with Hover Effect */}
        <motion.button
          onClick={onContinue}
          className="bg-slate-950 text-white px-6 py-2 mt-4 rounded-full hover:bg-slate-800 transition duration-300 focus:outline-none focus:ring focus:ring-slate-300"
          whileHover={{ scale: 1.01 }}
          whileTap={{ scale: 0.95 }}>
          Go to Home
        </motion.button>

        {/* Progress Bar for Redirect */}
      </motion.div>

      {/* Floating decorative elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-10 left-20 w-40 h-40 bg-purple-300 rounded-full filter blur-3xl opacity-50 animate-pulse"></div>
        <div className="absolute bottom-20 right-40 w-32 h-32 bg-pink-300 rounded-full filter blur-3xl opacity-50 animate-pulse"></div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
